import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { LocalizedText } from '@fame/shared';
import * as React from 'react';

interface MultiLanguageInputProps {
  value?: LocalizedText;
  onChange: (value: LocalizedText) => void;
  className?: string;
  placeholder?: string;
  ref?: React.Ref<HTMLInputElement>;
  onBlur?: () => void;
}

const languageTabs = ['nl', 'fr', 'en'] as const;

export function MultiLanguageInput({
  value = { nl: '', fr: '', en: '' },
  onChange,
  className,
  placeholder,
  ref,
  onBlur,
}: MultiLanguageInputProps) {
  const [activeTab, setActiveTab] = React.useState<'nl' | 'fr' | 'en' | null>(null);
  const initialTab = languageTabs.find(tab => (value[tab]?.trim().length ?? 0) > 0);
  const displayedTab = activeTab ?? initialTab ?? 'nl';

  const handleChange = (text: string) => {
    onChange({
      ...value,
      [displayedTab]: text,
    });
  };

  const tabs = [
    { id: 'nl', label: 'NL' },
    { id: 'fr', label: 'FR' },
    { id: 'en', label: 'EN' },
  ] as const;

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex space-x-1 border-b">
        {tabs.map(tab => {
          const hasContent = (value?.[tab.id]?.trim().length ?? 0) > 0;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'px-3 py-1 text-sm font-medium transition-colors border-b-2 mb-[-2px] relative',
                displayedTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              )}
            >
              {tab.label}
              {hasContent && (
                <span
                  className={cn(
                    'ml-1 mb-0.5 inline-block w-1.5 h-1.5 rounded-full',
                    displayedTab === tab.id ? 'bg-primary' : 'bg-muted-foreground'
                  )}
                />
              )}
            </button>
          );
        })}
      </div>
      <Input
        ref={ref}
        value={value?.[displayedTab] || ''}
        onChange={e => handleChange(e.target.value)}
        onBlur={onBlur}
        placeholder={`${placeholder || ''} (${displayedTab.toUpperCase()})`}
      />
    </div>
  );
}
